import "./Table.scss";

import React, { FunctionComponent, useState, useMemo, useRef, useEffect } from "react";
import { DataGrid, type GridPaginationModel, GridColumnHeaderParams, GridToolbarExport, GridRenderCellParams, useGridApiContext, useGridSelector, gridFilteredTopLevelRowCountSelector } from "@mui/x-data-grid";
import { SvgIcon, Box, IconButton, Select, MenuItem, Tooltip, SelectChangeEvent, Menu, ListItemText, ListItemIcon, CircularProgress, TooltipProps, tooltipClasses } from "@mui/material";
import { styled } from '@mui/material/styles';


import { ICustomFooter, ICustomToolbar, TableProps } from "./ITable";
import CustomToolbar from "./components/CustomToolbar";
import CustomFooter from "./components/CustomFooter";
import TableColumnsPanel from "./components/TableColumnsPanel";
import CommonCheckbox from "../Checkbox/CommonCheckbox";
import ProjectSelect from "../withoutControllerComponents/Select/Select";
import { ReactComponent as Search } from "../../Assets/Images/search.svg";
import { ReactComponent as NoData } from "../../Assets/Images/NoData.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import DraggableFilterPanel from "./components/DraggableFilterPanel";

// icons
import { ReactComponent as FileXlsx } from "../../Assets/Images/file_xlsx.svg";
import { ReactComponent as FileCsv } from "../../Assets/Images/file_csv.svg";

// Utility to extract plain text from React nodes (e.g. <b>Role</b> -> "Role")
const getTextFromJSX = (node: React.ReactNode): string => {
  if (!node) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (React.isValidElement(node)) {
    return React.Children.toArray((node.props as any).children)
      .map(getTextFromJSX)
      .join("");
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromJSX).join("");
  }
  return "";
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 13,
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #ccc',
    padding: '10px'
  },
}));

// Bridge component to use project's Select in MUI DataGrid slots
const BridgeSelect = (props: any) => {
  const { value, onChange, children, label, fullWidth, sx, disabled, ...others } = props;

  // Convert MUI MenuItems (children) to Autocomplete-friendly options
  const options = React.Children.map(children, (child: any) => {
    if (React.isValidElement(child)) {
      const childProps = child.props as any;
      return {
        label: getTextFromJSX(childProps.children),
        value: childProps.value,
      };
    }
    return null;
  })?.filter(Boolean) || [];

  const selectedOption = options.find((o: any) => o.value === value) || (value ? { label: value.toString(), value } : null);

  return (
    <ProjectSelect
      {...others}
      label={label}
      options={options}
      value={selectedOption}
      disabled={disabled}
      fullWidth={fullWidth}
      disableClearable={true}
      disablePortal={false}
      autoHighlight
      openOnFocus={true}
      blurOnSelect={true}
      forcePopupIcon={true}
      sx={{
        minWidth: 160,
        ...sx,
        "& .MuiInputBase-root": {
          backgroundColor: "#fff",
          "& fieldset": { border: "none" },
          "&:hover fieldset": { border: "none" },
          "&.Mui-focused fieldset": { border: "none" },
        },
        "& .MuiAutocomplete-inputRoot": {
          padding: "4px 8px !important",
          borderRadius: "8px",
          border: "1px solid rgba(145, 158, 171, 0.2)",
          "&:hover": { borderColor: "#009DFF" },
          "&.Mui-focused": { borderColor: "#009DFF", boxShadow: "0 0 0 2px rgba(0, 157, 255, 0.1)" }
        }
      }}
      onChange={(_event: any, newValue: any) => {
        if (onChange) {
          onChange({ target: { value: newValue?.value } } as any);
        }
      }}
      getOptionLabel={(option: any) => (typeof option === "string" ? option : option?.label || "")}
      isOptionEqualToValue={(option: any, value: any) => {
        if (!option || !value) return false;
        const optVal = typeof option === 'object' ? option.value : option;
        const valVal = typeof value === 'object' ? value.value : value;
        return optVal === valVal;
      }}
    />
  );
};

const Table: FunctionComponent<TableProps> = (props) => {
  const {
    rows,
    columns,
    density,
    loading,
    isCellEditable,
    columnVisibilityModel,
    checkboxSelection,
    getRowId,
    rowCount,
    rowHeight,
    children,
    initialPageSize,
    currentPage = 1,
    totalPage = 1,
    pageSize: propPageSize = initialPageSize ?? 10,
    onPageChange,
    pageSizeOptions,
    localeText,
    processRowUpdate,
    editMode,
    rowModesModel,
    onRowModesModelChange,
    onRowEditStop,
    getRowClassName,
    paginationMode = "client",
    initialSearchValue,
    disableColumnFilters = false,
    filterModel,
    onFilterModelChange,
    showManageColumns = true,
    showManageFilters = true,
    exportButton = true,
    exportFileName = "Export",
    disablePageSizeSelector = false,
    isExporting = false,
    onExport,
    autoHeight = false,
    sx = {
      boxShadow: "0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)",
      borderRadius: "12px",
      border: "1px solid rgba(145, 158, 171, 0.1)",
      "& .MuiDataGrid-row": { backgroundColor: "#fff" },
      "& .MuiDataGrid-row.Mui-hovered": { backgroundColor: "rgba(145, 158, 171, 0.08)" },
      "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(145, 158, 171, 0.08)" },
    },
  } = props;

  const executeExport = async (format: "csv" | "xlsx") => {
    if (onExport) {
      onExport(format);
      return;
    }

    try {
      const { utils, writeFile } = await import("xlsx");

      // Filter out 'action' columns and other non-exportable fields
      // We now include   columns even if they have a headerCheckbox (like View, Add, etc.)
      const exportableColumns = memoizedColumns.filter(col => col.field !== "action");

      // Map rows to clean objects with header names as keys
      const dataToExport = rows.map(row => {
        const entry: any = {};
        exportableColumns.forEach(col => {
          const header = col.headerName || col.field;
          let value = (row as any)[col.field];

          // Use valueFormatter if available
          if (col.valueFormatter) {
            value = col.valueFormatter({ value, row } as any);
          }

          entry[header] = value;
        });
        return entry;
      });

      const worksheet = utils.json_to_sheet(dataToExport);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Data");

      const fileName = `${exportFileName}_${new Date().toLocaleDateString()}.${format}`;
      writeFile(workbook, fileName, { bookType: format });
    } catch (error) {
      console.error("Export failed:", error);
    }
  };


  const memoizedColumns = useMemo(() => {
    return columns.map((col) => {
      // Default properties to prevent crunching
      const defaultProps = {
        minWidth: col.minWidth ?? (col.width ? undefined : 150),
        flex: col.flex ?? (col.width || col.minWidth ? undefined : 1),
      };

      // Extract text from renderHeader if possible for better filter menu labels
      let derivedHeaderName = col.headerName;
      if (!derivedHeaderName && col.renderHeader) {
        // We call renderHeader with a dummy params object just to extract the label
        try {
          const headerNode = col.renderHeader({} as any);
          derivedHeaderName = getTextFromJSX(headerNode);
        } catch (e) {
          // Fallback if renderHeader fails or expects specific params we can't provide
          derivedHeaderName = col.headerName || col.field || "";
        }
      }

      if (!derivedHeaderName) {
        derivedHeaderName = col.headerName || col.field || "";
      }

      // Pin action columns to the right
      const isActionColumn = col.field === "action" || col.type === "actions";

      if (col.headerCheckbox) {
        return {
          ...defaultProps,
          ...col,
          headerName: derivedHeaderName,
          // For checkbox columns, we usually want a fixed narrow width
          minWidth: col.minWidth ?? 50,
          flex: col.flex ?? 0,
          renderHeader: (params: GridColumnHeaderParams) => (
            <Box display="flex" alignItems="center" gap={1} justifyContent="center" width="100%">
              <CommonCheckbox
                color="success"
                checked={!!col.headerCheckboxChecked}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => col.onHeaderCheckboxChange?.(e.target.checked)}
              />
              {col.renderHeader ? col.renderHeader(params) : <b>{derivedHeaderName}</b>}
            </Box>
          ),
        };
      }

      const columnConfig: any = {
        ...defaultProps,
        ...col,
        headerName: derivedHeaderName,
      };

      // Configure action columns
      if (isActionColumn) {
        columnConfig.disableReorder = true;
        columnConfig.headerClassName = 'action-column-header';
        columnConfig.cellClassName = 'action-column-cell';
        columnConfig.width = col.width ?? 100;
      }

      // Wrap default text instances with LightTooltip if no custom renderCell is provided
      if (!col.renderCell && (!col.type || col.type === 'string')) {
        columnConfig.renderCell = (params: GridRenderCellParams) => {
          const text = params.formattedValue ?? (params.value != null ? String(params.value) : "-");
          return (
            <LightTooltip title={text}>
              <div className="MuiDataGrid-cellContent" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: '100%' }}>
                {text}
              </div>
            </LightTooltip>
          );
        };
      }

      // Wrap header with LightTooltip if no custom renderHeader is provided
      if (!col.renderHeader && derivedHeaderName && !col.headerCheckbox) {
        columnConfig.renderHeader = (params: GridColumnHeaderParams) => (
          <LightTooltip title={derivedHeaderName}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {derivedHeaderName}
            </span>
          </LightTooltip>
        );
      }

      return columnConfig;
    });
  }, [columns, getTextFromJSX]);

  // Classify children
  const customToolbar = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === CustomToolbar
  );

  const customFooter = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === CustomFooter
  );

  const customToolbarProps: ICustomToolbar | {} =
    React.isValidElement(customToolbar) && customToolbar.props
      ? (customToolbar.props as ICustomToolbar)
      : {};

  const customFooterProps: ICustomFooter | {} =
    React.isValidElement(customFooter) && customFooter.props
      ? (customFooter.props as ICustomFooter)
      : {};

  const tableRef = useRef<HTMLDivElement>(null);

  // Client-side pagination state
  const [clientPaginationModel, setClientPaginationModel] = useState<GridPaginationModel>({
    pageSize: initialPageSize ?? 10,
    page: 0,
  });

  const isServerMode = paginationMode === "server";

  const serverPaginationModel: GridPaginationModel = {
    page: currentPage - 1,
    pageSize: propPageSize,
  };

  const paginationModelToUse: GridPaginationModel = isServerMode ? serverPaginationModel : clientPaginationModel;

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (isServerMode) {
      onPageChange?.(model.page + 1);
    } else {
      setClientPaginationModel(model);
    }
  };

  const totalPagesToUse = isServerMode ? totalPage : Math.ceil((rows.length || 0) / paginationModelToUse.pageSize);
  const rowCountToUse = isServerMode ? (rowCount ?? 0) : undefined;
  const pageSizeOptionsToUse = isServerMode ? [propPageSize] : (pageSizeOptions ?? [5, 10, 20, 50]);

  // Custom footer with pagination arrows
  const FooterWithArrows: FunctionComponent = () => {
    const apiRef = useGridApiContext();
    const filteredRowCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);

    const actualRowCount = isServerMode ? (rowCount ?? rows.length) : filteredRowCount;
    const actualTotalPages = isServerMode ? (totalPage ?? 1) : Math.max(1, Math.ceil(actualRowCount / paginationModelToUse.pageSize));
    const currentPage = Math.max(0, Math.min(paginationModelToUse.page, actualTotalPages - 1));

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
        width="100%"
        sx={{
          backgroundColor: "#fff",
          borderTop: "1px solid #e0e0e0",
          borderLeft: "2px solid #e0e0e0",
          borderRight: "2px solid #e0e0e0",
          borderBottom: "2px solid #e0e0e0",
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          boxShadow: "0 4px 12px rgba(145, 158, 171, 0.15)",
        }}
      >
        {/* Left side: arrows */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() => {
              handlePaginationModelChange({
                ...paginationModelToUse,
                page: 0,
              });
            }}
            disabled={currentPage === 0}
            size="small"
            title="Go to First Page"
            sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
          >
            <FirstPageIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              handlePaginationModelChange({
                ...paginationModelToUse,
                page: Math.max(0, currentPage - 1),
              });
            }}
            disabled={currentPage === 0}
            size="small"
            title="Go to Previous Page"
            sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <span style={{ fontSize: "14px", fontWeight: "600", margin: "0 10px" }}>
            Page {currentPage + 1} of {actualTotalPages}
          </span>
          <IconButton
            onClick={() => {
              handlePaginationModelChange({
                ...paginationModelToUse,
                page: Math.min(currentPage + 1, actualTotalPages - 1),
              });
            }}
            disabled={currentPage + 1 >= actualTotalPages}
            size="small"
            title="Go to Next Page"
            sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              handlePaginationModelChange({
                ...paginationModelToUse,
                page: actualTotalPages - 1,
              });
            }}
            disabled={currentPage + 1 >= actualTotalPages}
            size="small"
            title="Go to Last Page"
            sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
          >
            <LastPageIcon fontSize="small" />
          </IconButton>

          {/* Rows per page selector - Now visible in all modes */}
          <Box display="flex" alignItems="center" gap={0.5} ml={2}>
            <span style={{ fontSize: "14px", color: "#637381", fontWeight: 500 }}>Rows per page:</span>
            <Select
              value={paginationModelToUse.pageSize}
              disabled={disablePageSizeSelector}
              onChange={(e: SelectChangeEvent<number>) => {
                handlePaginationModelChange({
                  ...paginationModelToUse,
                  pageSize: Number(e.target.value),
                  page: 0,
                });
              }}
              variant="standard"
              size="small"
              disableUnderline
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: disablePageSizeSelector ? "#919EAB" : "#212b36",
                cursor: disablePageSizeSelector ? "not-allowed" : "pointer",
                "& .MuiSelect-select": {
                  py: 0,
                  pr: "24px !important",
                  pl: "4px !important",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "18px",
                  right: 0,
                  color: disablePageSizeSelector ? "#919EAB" : "inherit"
                }
              }}
            >
              {pageSizeOptionsToUse.map((option) => {
                const value = typeof option === "number" ? option : option.value;
                const label = typeof option === "number" ? option : option.label;
                return (
                  <MenuItem key={value} value={value} sx={{ fontSize: "14px" }}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>

          {/* Total rows display */}
          <Box ml={3} display="flex" alignItems="center">
            <span style={{ fontSize: "14px", color: "#637381", fontWeight: 500 }}>
              Total: <span style={{ color: "#212b36", fontWeight: 600 }}>{actualRowCount}</span>
            </span>
          </Box>
        </Box>

        {/* Right side: optional custom footer or info */}
        <Box display="flex" alignItems="center" gap={1.5}>
          {isExporting ? (
            <Box display="flex" alignItems="center" gap={1} pr={1}>
              <CircularProgress size={20} sx={{ color: "#009DFF" }} />
              <span style={{ fontSize: "13px", color: "#637381", fontWeight: 500 }}>Exporting...</span>
            </Box>
          ) : (
            exportButton && rows.length > 0 && (
              <Box display="flex" gap={1.5}>
                <Tooltip title="Export as CSV">
                  <IconButton onClick={() => executeExport("csv")} className="export-btn export-btn--csv">
                    <FileCsv height={20} width={20} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export as Excel">
                  <IconButton onClick={() => executeExport("xlsx")} className="export-btn export-btn--xlsx">
                    <FileXlsx height={20} width={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          )}
          {customFooter && <CustomFooter {...customFooterProps} />}
        </Box>
      </Box>
    );
  };

  return (
    <div ref={tableRef} style={{ maxHeight: "calc(100vh - 80px)", height: "100%", width: "100%", maxWidth: "100%", minWidth: 0, minHeight: "225px", display: "flex", flexDirection: "column" }}>
      <DataGrid
        rows={rows}
        columns={memoizedColumns}
        disableVirtualization={false}
        editMode={editMode}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        onRowEditStop={onRowEditStop}
        getRowClassName={getRowClassName}
        localeText={{
          ...localeText,
          toolbarExport: "Export",
          toolbarExportCSV: "CSV",
          toolbarQuickFilterPlaceholder: "Search",
          columnMenuLabel: "Filter Options",
          filterPanelDeleteIconLabel: "Remove filters",
        }}
        sx={{
          flex: 1,
          ...sx
        } as any}
        isCellEditable={isCellEditable}
        className="Table"
        autoHeight={autoHeight}
        density={density ?? "standard"}
        loading={loading}
        disableColumnMenu={disableColumnFilters}
        disableColumnFilter={disableColumnFilters}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        checkboxSelection={checkboxSelection}
        getRowId={getRowId}
        rowHeight={rowHeight ?? 52}
        pagination
        paginationMode={paginationMode}
        paginationModel={paginationModelToUse}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={pageSizeOptionsToUse}
        rowCount={rowCountToUse}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: CustomToolbar,
          columnsPanel: TableColumnsPanel,
          filterPanel: DraggableFilterPanel,
          footer: FooterWithArrows,
          noRowsOverlay: CustomNoRows,
          quickFilterIcon: Search,
        }}
        slotProps={{
          toolbar: {
            ...customToolbarProps,
            showQuickFilter: true,
            exportCsv: { text: "CSV" },
            filterButton: !disableColumnFilters,
            showManageColumns,
            showManageFilters,
          } as any,
          filterPanel: {
            filterFormProps: {
              sx: { gap: "7px" },
              columnInputProps: { variant: "outlined", size: "small" },
              operatorInputProps: { variant: "outlined", size: "small" },
              valueInputProps: {
                InputComponentProps: { variant: "outlined", size: "small" },
              },
            } as any, // take care of props!
          },
        }}
        initialState={{
          columns: { columnVisibilityModel },
          filter: {
            filterModel: {
              items: [],
              quickFilterValues: initialSearchValue ? [initialSearchValue] : [],
            },
          },
        }}
      />
    </div>
  );
};

export default Table;

// ---------------- Custom No Rows Overlay ----------------
interface CustomNoRowsProps { }
const CustomNoRows: FunctionComponent<CustomNoRowsProps> = () => {
  return (
    <div className="custom_no_rows">
      <SvgIcon className="table_svg_icon" component={NoData} inheritViewBox />
      No Data Found
    </div>
  );
};